// First import: records the cross-domain hand-off and removes a message link
// token from the address bar before any other module reads the URL.
import "./Common/Tracking/utils/visitorScript";
import "react-native-gesture-handler";
import { registerRootComponent } from "expo";
import { initTelemetry } from "./Common/Telemetry/boot";
import { isClinicApp } from "./isClinicApp";
import { initializeCaptchaBypass } from "./Common/Utils/captchaBypass";
import { installChunkErrorReloadHandler } from "./Common/Updates/utils/chunkReload";
import { installSkiaSurfaceErrorReporter } from "./Common/Skia/utils/skiaSurfaceErrors";
import { installPageLifecycleMarker } from "./Common/Telemetry/utils/pageLifecycleMarker";
import { configureDotLottieWasm } from "./Common/Lottie/utils/dotLottieWasm";

// Recover (single reload) from stale code-split chunk failures after a redeploy,
// including the dynamic app imports below. Registered before those imports run.
installChunkErrorReloadHandler();

// Serve the Lottie WASM from our own origin before anything renders a Lottie,
// so no loader fetches it from cdn.jsdelivr.net ahead of tracking consent.
configureDotLottieWasm();

// Attribute Skia web surface failures. Skia creates its surface from a
// ResizeObserver callback, so the throw lands on window.onerror rather than in
// any React boundary, so a global listener is the only place to catch it
// (DHT-4858). Registered before the app mounts any canvas.
installSkiaSurfaceErrorReporter();

// Initialize captcha bypass detection (web only)
initializeCaptchaBypass();

// eslint-disable-next-line no-undef
if (!__DEV__) {
  initTelemetry();
}

// Report the previous document in this tab if it died without unloading (a
// renderer crash or a tab discard leaves no RUM event of its own), and start
// sampling the JS heap once a minute. Installed right after RUM so its first
// events land in this session; the SDK queues them until init completes.
installPageLifecycleMarker();

// CanvasKit is deliberately NOT loaded here. From November 2024 the clinic
// entry awaited LoadSkiaWeb before registering the app, because the note's
// drawing canvas imported Skia with nothing gating it and Skia's web module
// binds `global.CanvasKit` the moment it evaluates. That put a 7.6 MB (3.1 MB
// gzipped) download on every clinic boot and a permanent 128 MB wasm heap floor
// on every session, drawing or not. Every Skia surface now awaits CanvasKit
// itself the first time it mounts (the signature pad and image overlay via
// WithSkiaWeb, the note canvas via LazyPatientNoteDrawingCanvas.web.tsx), each
// inside a boundary that keeps a failed load in the field. A boot-time load
// must not come back: __tests__/skiaBootPath.test.ts fails if it does.
const registerApp = async () => {
  if (!isClinicApp) {
    const PatientApp = (await import("./PatientApp/PatientApp")).default;
    registerRootComponent(PatientApp);
  } else {
    const ClinicApp = (await import("./ClinicApp/App")).default;
    registerRootComponent(ClinicApp);
  }
};

registerApp();
